import type { ScreenShape } from "@slot/presentation";
import { CHROME_BANDS } from "../../chrome/chromeContentArea";

/**
 * A title's arrangement of the portrait control bar over the shell's (decision 78, `GameChromeDefinition.barLayout`): on a
 * wide phone a portrait composition fits the screen's height and leaves its sides empty, and a title that wants that
 * height for its scene moves the controls down. They keep their sizes, columns and order; the numbers' row stays.
 */
export interface ControlBarLayout {
  /**
   * Portrait ("mobile"), by the screen's width: the arrangement with the largest `minWidth` the screen reaches applies;
   * on a screen narrower than every one, the shell's places.
   */
  mobile?: readonly PortraitBarArrangement[];
}

/** One portrait arrangement: how far the controls move down from the shell's places, in design units. */
export interface PortraitBarArrangement {
  /** The narrowest screen it applies on: the viewport's width in design units (1080 at 9:16, more on a wider screen). */
  minWidth: number;
  /**
   * How far every control over the numbers' row moves down, the hold caption, the gamble button, the bet popup and the
   * feature HUD over the Spin button with them: up to `PORTRAIT_CONTROLS_DROP_MAX`.
   */
  controlsDrop?: number;
  /**
   * How much further the Buy Bonus button (and the feature mode's Disable in its place) moves down: up to
   * `PORTRAIT_PURCHASE_DROP_MAX`, and only on a screen at least `PORTRAIT_PURCHASE_DROP_MIN_WIDTH` wide.
   */
  purchaseDrop?: number;
}

/** The drops the bar applies on a screen, in design units. */
export interface PortraitBarDrops {
  controls: number;
  purchase: number;
}

/** The Spin button's slot per shape (the button scales its face to it), in design units. */
export const SPIN_SIZE: Record<ScreenShape, number> = { desktop: 240, mobile: 300, mobile_landscape: 239 };

/** The round toggles' size on every shape (menu, speed, autospin), in design units. */
export const TOGGLE_SIZE = 110;

/** The round bet buttons' size on the mobile shapes. */
export const BET_BUTTON_SIZE = 110;

/** The Buy Bonus button's size on every shape, and the feature mode's Disable in its place. */
export const PURCHASE_SIZE = 171.2;

/**
 * Where the mobile groups stand the controls an arrangement is bounded by, in design units: each centre's height over
 * the screen's bottom edge (portrait's places before an arrangement moves them; the left column's are landscape's too,
 * where none applies), the Buy Bonus button's centre from the left edge and the bet buttons' from the centre line. The
 * bar's stylesheet reads them here, so the bounds below are its own.
 */
export const MOBILE_BAR_PLACES = {
  purchaseLeft: 117.06,
  purchase: 485.5,
  menuToggle: 255.5,
  spin: 360.4,
  betOffset: 240,
  autospinToggle: 429.2,
  speedToggle: 255.5,
} as const;

const P = MOBILE_BAR_PLACES;

/** How far `controlsDrop` reaches: the lowest toggles (the menu's on the left, the speed's on the right) down on the numbers' band. */
export const PORTRAIT_CONTROLS_DROP_MAX = Math.min(P.menuToggle, P.speedToggle) - TOGGLE_SIZE / 2 - CHROME_BANDS.bottom;

/** How far `purchaseDrop` reaches: the Buy Bonus button down on the menu toggle under it. */
export const PORTRAIT_PURCHASE_DROP_MAX = P.purchase - PURCHASE_SIZE / 2 - (P.menuToggle + TOGGLE_SIZE / 2);

/** The gap the Buy Bonus button keeps from the bet's minus button beside it once it moves down. */
const PURCHASE_BET_CLEARANCE = 10;

/**
 * The narrowest screen a `purchaseDrop` applies on: the Buy Bonus button's box clear of the bet's minus button's by
 * `PURCHASE_BET_CLEARANCE` (the minus button stands `betOffset` left of the centre line, so the two close in as the screen
 * narrows, and in the shell's places their corners already overlap below about 995). Narrower, the button moves with the
 * others alone: no arrangement can put two controls over each other.
 */
export const PORTRAIT_PURCHASE_DROP_MIN_WIDTH = 2 * (P.purchaseLeft + PURCHASE_SIZE / 2 + PURCHASE_BET_CLEARANCE + P.betOffset + BET_BUTTON_SIZE / 2);

const NO_DROPS: PortraitBarDrops = { controls: 0, purchase: 0 };

/**
 * The drops the bar applies on a screen: its width's arrangement's, each within its reach, the Buy Bonus button's only
 * where it clears the bet's minus button; none off portrait, without a layout or on a screen narrower than every
 * arrangement.
 */
export function portraitBarDrops(layout: ControlBarLayout | undefined, view: { readonly shape: ScreenShape; readonly design: { readonly width: number } }): PortraitBarDrops {
  if (view.shape !== "mobile") return NO_DROPS;
  const width = view.design.width;
  let arrangement: PortraitBarArrangement | null = null;
  for (const candidate of layout?.mobile ?? []) {
    if (candidate.minWidth <= width && (!arrangement || candidate.minWidth > arrangement.minWidth)) arrangement = candidate;
  }
  if (!arrangement) return NO_DROPS;
  return {
    controls: within(arrangement.controlsDrop, PORTRAIT_CONTROLS_DROP_MAX),
    purchase: width >= PORTRAIT_PURCHASE_DROP_MIN_WIDTH ? within(arrangement.purchaseDrop, PORTRAIT_PURCHASE_DROP_MAX) : 0,
  };
}

/**
 * How high the portrait controls reach over the screen's bottom edge with these drops, in design units: the top of the
 * highest (the Buy Bonus button, the Spin button or the autospin toggle; 571.1 in the shell's places), the line a
 * portrait composition keeps its art above. The hold caption, the gamble button and the bet popup over the Spin button
 * reach higher while they show.
 */
export function portraitControlsTop(drops: PortraitBarDrops): number {
  return Math.max(P.purchase + PURCHASE_SIZE / 2 - drops.purchase, P.spin + SPIN_SIZE.mobile / 2, P.autospinToggle + TOGGLE_SIZE / 2) - drops.controls;
}

function within(drop: number | undefined, max: number): number {
  return Math.min(Math.max(drop ?? 0, 0), max);
}
